import React, {
  FunctionComponent,
  FormEvent,
  useState,
  ChangeEvent,
  useEffect,
  useContext,
} from "react";
import { useTranslate, sendTealiumEvent } from "../../../helpers";
import { Button, Choice, TextField, ChoiceList } from "@cmsgov/ds-medicare-gov";
import { Gender, DemographicInfo, ActionType } from "../../../@types";
import { AppContext } from "../../../app/store";

const GetMoreAccuratePrice: FunctionComponent = () => {
  const t = useTranslate();
  const {
    state: { demographicInfo },
    dispatch,
  } = useContext(AppContext);
  const { age: savedAge, gender: savedGender, tobacco: savedTobacco } =
    demographicInfo || {};
  const [age, setAge] = useState<string>(savedAge || "");
  const [gender, setGender] = useState<Gender | undefined>(savedGender);
  const [tobacco, setTobacco] = useState<boolean | undefined>(savedTobacco);
  const [under65, setUnder65] = useState<boolean | undefined>(undefined);
  const [error, setError] = useState("");

  const getValues = (): DemographicInfo => {
    const values = {} as DemographicInfo;
    age ? (values.age = age) : under65 ? (values.age = "64") : null;
    gender ? (values.gender = gender) : null;
    tobacco !== undefined ? (values.tobacco = tobacco) : null;
    return values;
  };

  const sendTealiumEvents = (): void => {
    if (age) {
      sendTealiumEvent(
        "Step 1: Decide what plans you want",
        "UI Option: Accuracy: Age"
      );
    }
    if (gender) {
      sendTealiumEvent(
        "Step 1: Decide what plans you want",
        `UI Option: Accuracy: Sex: ${
          gender === Gender.FEMALE ? "Female" : "Male"
        }`
      );
    }
    if (tobacco !== undefined) {
      sendTealiumEvent(
        "Step 1: Decide what plans you want",
        `UI Option: Accuracy: Do you use tobacco: ${tobacco ? "Yes" : "No"}`
      );
    }
    sendTealiumEvent(
      "Step 1: Decide what plans you want",
      "CTA: Update prices"
    );
  };

  const updateDemographicInfo = (clear = false): void => {
    dispatch({
      type: ActionType.UPDATE_DEMOGRAPHICS,
      payload: clear ? undefined : getValues(),
    });
    sendTealiumEvents();
  };

  const clearValues = (): void => {
    setGender(undefined);
    setAge("");
    setTobacco(undefined);
    updateDemographicInfo(true);
    setUnder65(false);
  };

  useEffect(() => {
    if (under65 !== undefined) {
      under65 &&
        sendTealiumEvent(
          "Step 1: Decide what plans you want",
          "UI Option: Show only plans for people under 65"
        );
      dispatch({
        type: ActionType.UPDATE_DEMOGRAPHICS,
        payload: getValues(),
      });
    }
  }, [under65]);

  return (
    <div className="mct-hide-print">
      <form
        tabIndex={-1}
        className="AccuratePriceContainer"
        onSubmit={(event: FormEvent<HTMLFormElement>): void => {
          event.preventDefault();
          if (!age && gender === undefined && tobacco === undefined) {
            setError(t("medigap.accurate_price.error"));
          } else {
            setError("");
            updateDemographicInfo();
          }
        }}
      >
        <div className="AccuratePriceContainer__heading">
          <h2 className="ds-u-font-size--h3">
            {t("medigap.get_more_accurate_price")}
          </h2>
          <p>{t("medigap.accurate_price.note")}</p>
        </div>
        <div className="AccuratePriceContainer__body">
          <TextField
            label={t("medigap.accurate_price.age")}
            labelClassName="ds-u-margin-top--0"
            size="small"
            numeric
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              const num = Number(event.target.value);
              setAge(isNaN(num) || num === 0 ? "" : Math.abs(num).toString());
            }}
            value={age}
            name="age"
          />
          <ChoiceList
            name="sex"
            type="radio"
            label={
              <span className="ds-u-text-transform--uppercase">
                {t("medigap.accurate_price.sex")}
              </span>
            }
            className="AccuratePriceContainer__inline_label"
            choices={[
              {
                label: t("enroll_form.male"),
                value: Gender.MALE,
                checked: gender === Gender.MALE,
              },
              {
                label: t("enroll_form.female"),
                value: Gender.FEMALE,
                checked: gender === Gender.FEMALE,
              },
            ]}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setGender(event.target.value as Gender);
            }}
          />
          <ChoiceList
            name="tobacco"
            type="radio"
            label={
              <span className="ds-u-text-transform--uppercase">
                {t("medigap.accurate_price.tobacco")}
              </span>
            }
            className="AccuratePriceContainer__inline_label"
            choices={[
              { label: t("yes"), value: "Y", checked: tobacco },
              { label: t("no"), value: "N", checked: tobacco === false },
            ]}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setTobacco(event.target.value === "Y");
            }}
          />
        </div>
        <div className="AccuratePriceContainer__action">
          {error && (
            <p role="alert" className="ds-u-color--error">
              {error}
            </p>
          )}
          <Button variation="primary" type="submit">
            {t("medigap.accurate_price.update")}
          </Button>
          <Button
            variation="transparent"
            className="ds-c-button--primary-alt"
            type="button"
            onClick={clearValues}
          >
            {t("medigap.accurate_price.clear")}
          </Button>
        </div>
      </form>
      {!age && (
        <div>
          <Choice
            type="checkbox"
            name="under-65"
            value="Y"
            checked={under65 || false}
            onChange={(): void => {
              setUnder65(!under65);
            }}
          >
            {t("medigap.show_plans_for_under_65")}
          </Choice>
        </div>
      )}
    </div>
  );
};

export default GetMoreAccuratePrice;
